import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { Metric, Provider } from '../types';
import { computeScores } from '../utils/calculations';
import type { WorkplaceLevel, SealDefinition, IstSelection, SovScoreResult, SovWeights, SovObjective, OperationsAnswers, DeploymentModel } from '../types/workplace';
import providerDescriptions from '../assets/workplaceProviderDescriptions.json';

type ExportReportParams = {
  metrics: Metric[];
  activeProviders: Provider[];
  weights: Record<string, number>;
  currentCloudUsage: Record<string, boolean>;
  barCaptureRef: React.RefObject<HTMLDivElement | null>;
  radarCaptureRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Exportiert einen vollständigen PDF-Report mit Charts und Provider-Details
 */
export async function exportReport(params: ExportReportParams): Promise<void> {
  const { metrics, activeProviders, weights, currentCloudUsage, barCaptureRef, radarCaptureRef } = params;
  const maxPerMetric = 10;

  // Hilfsfunktion: HTML-Element als Bild capturen
  const captureNode = async (node: HTMLDivElement | null, wMM = 180) => {
    if (!node) return null;
    const canvas = await html2canvas(node, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });
    const dataUrl = canvas.toDataURL("image/png");
    return { dataUrl, pxW: canvas.width, pxH: canvas.height, targetWmm: wMM };
  };

  // Hilfsfunktion: Provider-Score berechnen
  const calcProviderScore = (provId: string) => {
    const prov = activeProviders.find((p) => p.id === provId);
    if (!prov) return { percent: 0, num: 0, den: 1 };
    let num = 0;
    let den = 0;
    for (const m of metrics) {
      const w = Math.max(1, Math.min(5, weights[m.id] ?? m.weightDefault ?? 3));
      const s = prov.scores[m.id] ?? 0;
      num += s * w;
      den += w * maxPerMetric;
    }
    const percent = den ? Math.round((num / den) * 100) : 0;
    return { percent, num, den };
  };

  // Daten vorbereiten
  const ranked = computeScores(metrics, activeProviders, weights, currentCloudUsage);

  // Charts aus den unsichtbaren Containern capturen
  const barShot = await captureNode(barCaptureRef.current);
  const radarShot = await captureNode(radarCaptureRef.current);

  // PDF initialisieren (A4 Hochformat, mm)
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;

  // ============= Deckblatt =============
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Sovereign Cloud – Bewertungsreport", margin, 30);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(
    [
      "Vielen Dank, dass Sie diese Demo verwenden.",
      "Dieser Report fasst Ihre Auswahl und Bewertung zusammen.",
      "",
      "Hinweis: Die Inhalte sind eine Demo und können unvollständig oder fehlerhaft sein.",
      "Bitte nutzen Sie die Ergebnisse nicht als alleinige Entscheidungsgrundlage.",
      "",
      "Viel Spaß beim Lesen!",
    ],
    margin,
    40,
  );

  doc.setFontSize(10);
  const ts = new Date().toLocaleString();
  doc.text(`Erstellt am: ${ts}`, margin, pageH - margin);

  // ============= Übersicht (Charts) =============
  doc.addPage();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Übersicht", margin, 20);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(
    "Ranking-Balkendiagramm (links) und Radar (rechts) basierend auf Ihrer aktuellen Auswahl.",
    margin,
    28,
  );

  // Balkenbild
  if (barShot?.dataUrl) {
    const availW = (pageW - margin * 2) / 2 - 5;
    const imgW = Math.min(availW, barShot.targetWmm);
    const ratio = barShot.pxH / barShot.pxW;
    const imgH = imgW * ratio;
    doc.addImage(barShot.dataUrl, "PNG", margin, 40, imgW, imgH, undefined, "FAST");
  }

  // Radarbild
  if (radarShot?.dataUrl) {
    const availW = (pageW - margin * 2) / 2 - 5;
    const imgW = Math.min(availW, radarShot.targetWmm);
    const ratio = radarShot.pxH / radarShot.pxW;
    const imgH = imgW * ratio;
    const x = margin + ((pageW - margin * 2) / 2) + 5;
    doc.addImage(radarShot.dataUrl, "PNG", x, 40, imgW, imgH, undefined, "FAST");
  }

  // Kleine Ranking-Tabelle
  autoTable(doc, {
    startY: 40 + 80,
    margin: { left: margin, right: margin },
    head: [["#", "Provider", "Gesamtscore (%)"]],
    body: ranked.map((r, i) => [String(i + 1), r.name, `${r.score}%`]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [255, 106, 76] },
    theme: "striped",
  });

  // ============= Detailseiten je Provider =============
  for (const r of ranked) {
    const prov = activeProviders.find((p) => p.id === r.id);
    if (!prov) continue;

    // Gründe (falls vorhanden)
    const reasons: Record<string, string> =
      (prov as any).reasons && typeof (prov as any).reasons === "object"
        ? (prov as any).reasons
        : {};

    const { percent } = calcProviderScore(prov.id);

    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(prov.name, margin, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(`Gesamtscore (gewichtet): ${percent}%`, margin, 28);

    // Tabellendaten: Merkmal, Score (0..10), Gewicht (1..5), gewichtet, Begründung
    const rows = metrics.map((m) => {
      const score = prov.scores[m.id] ?? 0;
      const w = Math.max(1, Math.min(5, weights[m.id] ?? m.weightDefault ?? 3));
      const weightedRaw = score * w;
      const reason = (reasons[m.id] || "").toString().trim();

      return [
        m.label,
        String(score),
        String(w),
        String(weightedRaw),
        reason.length > 0 ? reason : "-",
      ];
    });

    autoTable(doc, {
      startY: 36,
      margin: { left: margin, right: margin },
      head: [["Merkmal", "Score", "Gewicht", "Score×Gew.", "Begründung"]],
      body: rows,
      styles: { fontSize: 9, cellPadding: 2, valign: "top" },
      headStyles: { fillColor: [255, 106, 76] },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 14, halign: "right" },
        2: { cellWidth: 18, halign: "right" },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: "auto" },
      },
      theme: "striped",
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4 && typeof data.cell.raw === "string") {
          const s = data.cell.raw as string;
          if (s.length > 1200) data.cell.text = [s.slice(0, 1200) + " …"];
        }
      },
    });
  }

  // ============= Abschlussseite =============
  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Vielen Dank!", margin, 25);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(
    [
      "Ich freue mich über Ihr Feedback.",
      "Wenn es Interesse an einem detaillierteren Austausch gibt, melden Sie sich gerne:",
      "E-Mail: david@schneider4me.de",
      "",
      "Wenn Sie sich für das Thema Sovereign Cloud interessieren,",
      "lesen Sie gerne meinen Blog-Beitrag auf www.crayon.com.",
    ],
    margin,
    35,
  );

  // Speichern
  doc.save(`Sovereign-Cloud-Report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Alternative PDF-Export-Funktion (einfachere Version)
 */
export function generateSimplePdf(params: ExportReportParams): void {
  const { metrics, activeProviders, weights, currentCloudUsage } = params;

  if (!metrics.length || !activeProviders.length) {
    alert("Keine Daten vorhanden (Merkmale/Anbieter).");
    return;
  }

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  const marginY = 40;
  const line = (y: number) => doc.line(marginX, y, 595 - marginX, y);

  // Deckblatt
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Sovereign Cloud – Auswertung", marginX, marginY + 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const today = new Date().toLocaleDateString("de-DE");
  doc.text(`Datum: ${today}`, marginX, marginY + 32);
  doc.text(`Anzahl Merkmale: ${metrics.length}`, marginX, marginY + 48);
  doc.text(`Anzahl Anbieter (aktiv): ${activeProviders.length}`, marginX, marginY + 64);

  line(marginY + 74);

  // Gewichte-Tabelle
  const weightRows = metrics.map((m) => {
    const w = Math.max(1, Math.min(5, (weights[m.id] ?? m.weightDefault ?? 3)));
    return [m.label, String(w)];
  });

  autoTable(doc, {
    startY: marginY + 90,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [255, 106, 76] },
    head: [["Merkmal", "Gewicht (1–5)"]],
    body: weightRows,
    theme: "grid",
    margin: { left: marginX, right: marginX },
  });

  // Ranking-Tabelle
  const ranked = computeScores(metrics, activeProviders, weights, currentCloudUsage);
  const startY2 = (doc as any).lastAutoTable?.finalY
    ? (doc as any).lastAutoTable.finalY + 20
    : marginY + 90;

  autoTable(doc, {
    startY: startY2,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [255, 106, 76] },
    head: [["Platz", "Anbieter", "Score (%)"]],
    body: ranked.map((r, i) => [String(i + 1), r.name, String(r.score)]),
    theme: "grid",
    margin: { left: marginX, right: marginX },
  });

  // Detailseiten pro Anbieter
  activeProviders.forEach((prov: Provider) => {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(`Anbieter: ${prov.name}`, marginX, marginY + 10);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      "Begründungen pro Merkmal (Score + Text aus providers.json):",
      marginX,
      marginY + 28
    );
    line(marginY + 36);

    const detailRows: any[] = metrics.map((m) => {
      const score = prov.scores[m.id] ?? 0;
      const reason = prov.reasons?.[m.id] ?? "";
      return [m.label, String(score), reason];
    });

    autoTable(doc, {
      startY: marginY + 48,
      styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
      columnStyles: {
        0: { cellWidth: 180 },
        1: { cellWidth: 50, halign: "center" },
        2: { cellWidth: "auto" },
      },
      headStyles: { fillColor: [255, 106, 76] },
      head: [["Merkmal", "Score (0–10)", "Begründung"]],
      body: detailRows,
      theme: "grid",
      margin: { left: marginX, right: marginX },
    });
  });

  // Speichern
  const fileName = `Sovereign_Cloud_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
}

/**
 * Generates the full PDF report and returns it as a raw Base64 string.
 * Reuses the same logic as exportReport but does not trigger a browser download.
 */
export async function generatePdfBase64(params: ExportReportParams): Promise<string> {
  const { metrics, activeProviders, weights, currentCloudUsage, barCaptureRef, radarCaptureRef } = params;
  const maxPerMetric = 10;

  const captureNode = async (node: HTMLDivElement | null, wMM = 180) => {
    if (!node) return null;
    const canvas = await html2canvas(node, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });
    const dataUrl = canvas.toDataURL("image/png");
    return { dataUrl, pxW: canvas.width, pxH: canvas.height, targetWmm: wMM };
  };

  const calcProviderScore = (provId: string) => {
    const prov = activeProviders.find((p) => p.id === provId);
    if (!prov) return { percent: 0, num: 0, den: 1 };
    let num = 0;
    let den = 0;
    for (const m of metrics) {
      const w = Math.max(1, Math.min(5, weights[m.id] ?? m.weightDefault ?? 3));
      const s = prov.scores[m.id] ?? 0;
      num += s * w;
      den += w * maxPerMetric;
    }
    const percent = den ? Math.round((num / den) * 100) : 0;
    return { percent, num, den };
  };

  const ranked = computeScores(metrics, activeProviders, weights, currentCloudUsage);

  const barShot = await captureNode(barCaptureRef.current);
  const radarShot = await captureNode(radarCaptureRef.current);

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;

  // ============= Deckblatt =============
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Sovereign Cloud – Bewertungsreport", margin, 30);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(
    [
      "Vielen Dank, dass Sie diese Demo verwenden.",
      "Dieser Report fasst Ihre Auswahl und Bewertung zusammen.",
      "",
      "Hinweis: Die Inhalte sind eine Demo und können unvollständig oder fehlerhaft sein.",
      "Bitte nutzen Sie die Ergebnisse nicht als alleinige Entscheidungsgrundlage.",
      "",
      "Viel Spaß beim Lesen!",
    ],
    margin,
    40,
  );

  doc.setFontSize(10);
  const ts = new Date().toLocaleString();
  doc.text(`Erstellt am: ${ts}`, margin, pageH - margin);

  // ============= Übersicht (Charts) =============
  doc.addPage();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Übersicht", margin, 20);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(
    "Ranking-Balkendiagramm (links) und Radar (rechts) basierend auf Ihrer aktuellen Auswahl.",
    margin,
    28,
  );

  if (barShot?.dataUrl) {
    const availW = (pageW - margin * 2) / 2 - 5;
    const imgW = Math.min(availW, barShot.targetWmm);
    const ratio = barShot.pxH / barShot.pxW;
    const imgH = imgW * ratio;
    doc.addImage(barShot.dataUrl, "PNG", margin, 40, imgW, imgH, undefined, "FAST");
  }

  if (radarShot?.dataUrl) {
    const availW = (pageW - margin * 2) / 2 - 5;
    const imgW = Math.min(availW, radarShot.targetWmm);
    const ratio = radarShot.pxH / radarShot.pxW;
    const imgH = imgW * ratio;
    const x = margin + ((pageW - margin * 2) / 2) + 5;
    doc.addImage(radarShot.dataUrl, "PNG", x, 40, imgW, imgH, undefined, "FAST");
  }

  autoTable(doc, {
    startY: 40 + 80,
    margin: { left: margin, right: margin },
    head: [["#", "Provider", "Gesamtscore (%)"]],
    body: ranked.map((r, i) => [String(i + 1), r.name, `${r.score}%`]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [255, 106, 76] },
    theme: "striped",
  });

  // ============= Detailseiten je Provider =============
  for (const r of ranked) {
    const prov = activeProviders.find((p) => p.id === r.id);
    if (!prov) continue;

    const reasons: Record<string, string> =
      (prov as any).reasons && typeof (prov as any).reasons === "object"
        ? (prov as any).reasons
        : {};

    const { percent } = calcProviderScore(prov.id);

    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(prov.name, margin, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(`Gesamtscore (gewichtet): ${percent}%`, margin, 28);

    const rows = metrics.map((m) => {
      const score = prov.scores[m.id] ?? 0;
      const w = Math.max(1, Math.min(5, weights[m.id] ?? m.weightDefault ?? 3));
      const weightedRaw = score * w;
      const reason = (reasons[m.id] || "").toString().trim();

      return [
        m.label,
        String(score),
        String(w),
        String(weightedRaw),
        reason.length > 0 ? reason : "-",
      ];
    });

    autoTable(doc, {
      startY: 36,
      margin: { left: margin, right: margin },
      head: [["Merkmal", "Score", "Gewicht", "Score×Gew.", "Begründung"]],
      body: rows,
      styles: { fontSize: 9, cellPadding: 2, valign: "top" },
      headStyles: { fillColor: [255, 106, 76] },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 14, halign: "right" },
        2: { cellWidth: 18, halign: "right" },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: "auto" },
      },
      theme: "striped",
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4 && typeof data.cell.raw === "string") {
          const s = data.cell.raw as string;
          if (s.length > 1200) data.cell.text = [s.slice(0, 1200) + " …"];
        }
      },
    });
  }

  // ============= Abschlussseite =============
  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Vielen Dank!", margin, 25);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(
    [
      "Ich freue mich über Ihr Feedback.",
      "Wenn es Interesse an einem detaillierteren Austausch gibt, melden Sie sich gerne:",
      "E-Mail: david@schneider4me.de",
      "",
      "Wenn Sie sich für das Thema Sovereign Cloud interessieren,",
      "lesen Sie gerne meinen Blog-Beitrag auf www.crayon.com.",
    ],
    margin,
    35,
  );

  // Return raw Base64 instead of triggering download
  const dataUri = doc.output('datauristring');
  // Strip the "data:application/pdf;filename=...;base64," prefix
  const base64 = dataUri.split(',')[1];
  return base64;
}


/* ─── Workplace PDF Report ─────────────────────────────────────────── */

type WorkplacePdfParams = {
  levels: WorkplaceLevel[];
  istSelections: Record<string, IstSelection>;
  zielSelections: Record<string, IstSelection>;
  istScore: SovScoreResult;
  zielScore: SovScoreResult;
  sovWeights: SovWeights;
  sovObjectives: SovObjective[];
  sealDefinitions: SealDefinition[];
  infrastructureMode: string | null;
  istOperationsAnswers: OperationsAnswers;
  zielOperationsAnswers: OperationsAnswers;
  reportName?: string;
};

const DEPLOYMENT_LABELS: Record<string, string> = {
  'saas': 'SaaS',
  'hosted-eu': 'Hosted EU',
  'hosted-non-eu': 'Hosted Non-EU',
  'self-hosted': 'Self-hosted',
};

function formatDeployment(dep: DeploymentModel | null | undefined): string {
  if (!dep) return '—';
  return DEPLOYMENT_LABELS[dep] ?? dep;
}

function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—';
  return score.toFixed(2);
}

function sealLevelName(level: number | null, sealDefinitions: SealDefinition[]): string {
  if (level === null) return '—';
  const def = sealDefinitions.find(d => d.level === level);
  return def ? `Level ${level}: ${def.name}` : `Level ${level}`;
}

/**
 * Build the Ist/Ziel overview table rows with level group headers.
 */
function buildStateTableRows(
  levels: WorkplaceLevel[],
  selections: Record<string, IstSelection>,
  scoreResult: SovScoreResult,
): (string | { content: string; colSpan: number; styles: Record<string, unknown> })[][] {
  const rows: any[][] = [];
  for (const level of levels) {
    // Level group header row
    rows.push([{
      content: level.title,
      colSpan: 4,
      styles: { fontStyle: 'bold', fillColor: [230, 230, 230] as [number, number, number], textColor: [40, 40, 40] as [number, number, number] },
    }]);
    for (const sp of (level.subPoints ?? [])) {
      const sel = selections[sp.id];
      const fgScore = scoreResult.perFeatureGroup[sp.id];
      if (sp.type === 'operations-scoring') {
        rows.push([
          sp.title,
          'Operations Scoring',
          '—',
          formatScore(fgScore?.sovScore),
        ]);
      } else if (sel) {
        rows.push([
          sp.title,
          sel.provider,
          formatDeployment(sel.deployment),
          formatScore(fgScore?.sovScore),
        ]);
      } else {
        rows.push([sp.title, '—', '—', '—']);
      }
    }
  }
  return rows;
}

/**
 * Generates a Workplace Sovereignty PDF report and returns it as a Base64 string.
 * Comprehensive 6-page report covering Ist-Zustand, Ziel-Zustand, SOV-Objectives, and methodology.
 */
export function generateWorkplacePdfBase64(params: WorkplacePdfParams): string {
  const {
    levels, istSelections, zielSelections, istScore, zielScore,
    sovWeights, sovObjectives, sealDefinitions, infrastructureMode,
    reportName,
  } = params;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const tealRGB: [number, number, number] = [44, 143, 146];

  const istSealLevel = istScore.sealLevel;
  const zielSealLevel = zielScore.sealLevel;

  // ════════════════════════════════════════════════════════════════════
  // Page 1: Cover Page
  // ════════════════════════════════════════════════════════════════════
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Sovereign Workplace – Bewertungsreport', margin, 35);

  // Show report name below title if provided
  if (reportName && reportName.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(44, 143, 146);
    doc.text(reportName.trim(), margin, 44);
    doc.setTextColor(0, 0, 0);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(
    [
      'Dieser Report dokumentiert die Souveränitätsbewertung Ihres',
      'digitalen Arbeitsplatzes. Er vergleicht den aktuellen Ist-Zustand',
      'mit dem angestrebten Ziel-Zustand und zeigt das Verbesserungs-',
      'potenzial auf Basis des SEAL-Frameworks.',
      '',
      'Hinweis: Die Inhalte dienen als Orientierung und ersetzen',
      'keine individuelle Beratung.',
    ],
    margin,
    48,
  );

  // Ist-Score
  let yPos = 95;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Ist-Zustand', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  const istScoreText = istScore.overallScore !== null
    ? `SOV-Score: ${istScore.overallScore.toFixed(2)}  —  ${sealLevelName(istSealLevel, sealDefinitions)}`
    : 'SOV-Score: —';
  doc.text(istScoreText, margin, yPos + 8);

  // Ziel-Score
  yPos = 115;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Ziel-Zustand', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  const zielScoreText = zielScore.overallScore !== null
    ? `SOV-Score: ${zielScore.overallScore.toFixed(2)}  —  ${sealLevelName(zielSealLevel, sealDefinitions)}`
    : 'SOV-Score: —';
  doc.text(zielScoreText, margin, yPos + 8);

  // Delta
  yPos = 138;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  if (istScore.overallScore !== null && zielScore.overallScore !== null) {
    const delta = zielScore.overallScore - istScore.overallScore;
    const sign = delta >= 0 ? '+' : '';
    doc.text(`Verbesserung (Delta): ${sign}${delta.toFixed(2)}`, margin, yPos);
  } else {
    doc.text('Verbesserung (Delta): —', margin, yPos);
  }

  // Timestamp
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Erstellt am: ${new Date().toLocaleString('de-DE')}`, margin, pageH - margin);

  // ════════════════════════════════════════════════════════════════════
  // Page 2: Ist-Zustand Übersicht
  // ════════════════════════════════════════════════════════════════════
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Ist-Zustand Übersicht', margin, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const infraLabel = infrastructureMode
    ? `Infrastruktur-Modus: ${infrastructureMode.charAt(0).toUpperCase() + infrastructureMode.slice(1)}`
    : 'Infrastruktur-Modus: —';
  doc.text(infraLabel, margin, 28);

  const istRows = buildStateTableRows(levels, istSelections, istScore);

  autoTable(doc, {
    startY: 33,
    margin: { left: margin, right: margin },
    head: [['Unterpunkt', 'Provider', 'Deployment', 'SOV-Score']],
    body: istRows,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: tealRGB },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 55 },
      2: { cellWidth: 35 },
      3: { cellWidth: 25, halign: 'right' },
    },
    theme: 'striped',
  });

  // ════════════════════════════════════════════════════════════════════
  // Page 3: Ziel-Zustand Übersicht
  // ════════════════════════════════════════════════════════════════════
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Ziel-Zustand Übersicht', margin, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(infraLabel, margin, 28);

  const zielRows = buildStateTableRows(levels, zielSelections, zielScore);

  autoTable(doc, {
    startY: 33,
    margin: { left: margin, right: margin },
    head: [['Unterpunkt', 'Provider', 'Deployment', 'SOV-Score']],
    body: zielRows,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: tealRGB },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 55 },
      2: { cellWidth: 35 },
      3: { cellWidth: 25, halign: 'right' },
    },
    theme: 'striped',
  });

  // ════════════════════════════════════════════════════════════════════
  // Page 4: Anbieter-Steckbriefe (Provider Profiles)
  // ════════════════════════════════════════════════════════════════════
  const descriptions = providerDescriptions as Record<string, string>;

  // Collect unique provider names from both Ist and Ziel selections
  const allProviderNames = new Set<string>();
  for (const sel of Object.values(istSelections)) {
    if (sel?.provider && !sel.provider.startsWith('Other:')) {
      allProviderNames.add(sel.provider);
    }
  }
  for (const sel of Object.values(zielSelections)) {
    if (sel?.provider && !sel.provider.startsWith('Other:')) {
      allProviderNames.add(sel.provider);
    }
  }

  const sortedProviders = Array.from(allProviderNames).sort((a, b) => a.localeCompare(b, 'de'));

  if (sortedProviders.length > 0) {
    doc.addPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Anbieter-Steckbriefe', margin, 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Kurzprofile der in Ihrer Konfiguration verwendeten Anbieter', margin, 28);

    const profileRows: string[][] = sortedProviders.map((name) => {
      const desc = descriptions[name];
      return [name, desc || '—'];
    });

    autoTable(doc, {
      startY: 33,
      margin: { left: margin, right: margin },
      head: [['Provider', 'Beschreibung']],
      body: profileRows,
      styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: tealRGB },
      columnStyles: {
        0: { cellWidth: 45, fontStyle: 'bold' },
        1: { cellWidth: 'auto' },
      },
      theme: 'striped',
    });
  }

  // ════════════════════════════════════════════════════════════════════
  // Page 5: SOV-Objectives Vergleich (Ist vs Ziel)
  // ════════════════════════════════════════════════════════════════════
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('SOV-Objectives Vergleich (Ist vs Ziel)', margin, 20);

  const objectiveRows: string[][] = sovObjectives.map((obj) => {
    const weight = sovWeights[obj.id] ?? 0;
    const istVal = istScore.perObjective[obj.id];
    const zielVal = zielScore.perObjective[obj.id];
    const istNum = istVal !== undefined && istVal !== null ? istVal : null;
    const zielNum = zielVal !== undefined && zielVal !== null ? zielVal : null;
    const delta = (istNum !== null && zielNum !== null) ? zielNum - istNum : null;

    return [
      obj.name,
      `${(weight * 100).toFixed(0)}%`,
      istNum !== null ? istNum.toFixed(2) : '—',
      zielNum !== null ? zielNum.toFixed(2) : '—',
      delta !== null ? (delta >= 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2)) : '—',
    ];
  });

  autoTable(doc, {
    startY: 28,
    margin: { left: margin, right: margin },
    head: [['SOV-Objective', 'Gewicht', 'Ist', 'Ziel', 'Delta']],
    body: objectiveRows,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: tealRGB },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 25, halign: 'right' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 25, halign: 'right' },
    },
    theme: 'striped',
  });

  // Summary row below the table
  const tableEndY = (doc as any).lastAutoTable?.finalY ?? 120;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(
    `Gesamt Ist: ${formatScore(istScore.overallScore)}    |    Gesamt Ziel: ${formatScore(zielScore.overallScore)}`,
    margin,
    tableEndY + 10,
  );

  // ════════════════════════════════════════════════════════════════════
  // Page 5: Berechnungsmethodik
  // ════════════════════════════════════════════════════════════════════
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Berechnungsmethodik', margin, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  let methodY = 30;

  doc.setFont('helvetica', 'bold');
  doc.text('SEAL-Framework', margin, methodY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  methodY += 7;
  const sealExplanation = [
    'Das SEAL-Framework (Sovereignty Evaluation and Assessment Levels)',
    'bewertet die digitale Souveränität von Arbeitsplatzlösungen auf einer',
    'Skala von 0 (keine Souveränität) bis 4 (volle digitale Souveränität).',
  ];
  doc.text(sealExplanation, margin, methodY);
  methodY += 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Scoring-Methode', margin, methodY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  methodY += 7;
  const scoringExplanation = [
    'Jeder Provider wird pro Deployment-Modell auf 8 SOV-Objectives (0-4)',
    'bewertet. Der Gesamtscore ist der gewichtete Durchschnitt aller',
    'bewerteten Unterpunkte.',
  ];
  doc.text(scoringExplanation, margin, methodY);
  methodY += 20;

  // SEAL level definitions table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('SEAL-Level Definitionen', margin, methodY);
  methodY += 5;

  const sorted = [...sealDefinitions].sort((a, b) => a.level - b.level);
  const sealRows: string[][] = sorted.map((d) => [
    String(d.level),
    d.name,
    d.definition,
  ]);

  autoTable(doc, {
    startY: methodY,
    margin: { left: margin, right: margin },
    head: [['Level', 'Name', 'Definition']],
    body: sealRows,
    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: tealRGB },
    columnStyles: {
      0: { cellWidth: 14 },
      1: { cellWidth: 45 },
      2: { cellWidth: 'auto' },
    },
    theme: 'striped',
  });

  const sealTableEndY = (doc as any).lastAutoTable?.finalY ?? methodY + 60;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Operations Scoring (4.2)', margin, sealTableEndY + 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(
    [
      'Der Unterpunkt 4.2 (Operations Scoring) wird über 4 Fragen bewertet,',
      'die jeweils auf SOV-Objectives gemappt sind. Die Antworten (SEAL 0–4)',
      'fließen direkt als Objective-Scores in die Gesamtbewertung ein.',
    ],
    margin,
    sealTableEndY + 17,
  );

  // ════════════════════════════════════════════════════════════════════
  // Page 6: Closing Page
  // ════════════════════════════════════════════════════════════════════
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Vielen Dank!', margin, 25);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(
    [
      'Bei Fragen oder Interesse an einer detaillierten Beratung',
      'melden Sie sich gerne:',
      '',
      'E-Mail: david@schneider4me.de',
      '',
      'Mehr zum Thema Sovereign Workplace finden Sie auf www.crayon.com.',
    ],
    margin,
    35,
  );

  // Return Base64
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.split(',')[1];
  return base64;
}
